import React, { useEffect, useMemo, useState } from "react";
import "../css/Lp2App.css";
import { bootloopClient, SearchAuditDashboardDto } from "../api/bootloopClient";

const AdminSearchAudit: React.FC = () => {
  const [daysBack, setDaysBack] = useState(30);
  const [topN, setTopN] = useState(10);
  const [maxRows, setMaxRows] = useState(200);

  const [sizeUk, setSizeUk] = useState<string>("");     // optional filter
  const [outward, setOutward] = useState<string>("");   // optional filter
  const [shoeType, setShoeType] = useState<string>(""); // optional filter
  const [onlyZeroResults, setOnlyZeroResults] = useState(false);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [data, setData] = useState<SearchAuditDashboardDto | null>(null);

  const parsedSizeUk = useMemo(() => {
    const t = sizeUk.trim();
    if (!t) return undefined;
    const n = Number(t);
    return Number.isFinite(n) ? n : undefined;
  }, [sizeUk]);

  const parsedShoeType = useMemo(() => {
    const t = shoeType.trim();
    if (!t) return undefined;
    const n = Number(t);
    // allow only integers 0..4
    if (!Number.isFinite(n)) return undefined;
    const i = Math.trunc(n);
    return i >= 0 && i <= 4 ? i : undefined;
  }, [shoeType]);

  const parsedOutward = useMemo(() => {
    const t = outward.trim();
    if (!t) return undefined;
    return t.toUpperCase();
  }, [outward]);

  async function load() {
    setErr(null);
    try {
      setBusy(true);

      const dto = await bootloopClient.getSearchAuditDashboard({
        daysBack,
        topN,
        maxRows,
        sizeUk: parsedSizeUk,
        outward: parsedOutward,
        shoeType: parsedShoeType,
        onlyZeroResults,
      });

      setData(dto);
    } catch (e: any) {
      setErr(e?.message || "Failed to load dashboard.");
      setData(null);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const k = data?.kpis;

  return (
    <div className="lp2app">
      <h1 className="lp2app__title">Admin · Search Audit</h1>
      <p className="lp2app__lead">
        What users search for (demand), where they search (outward codes), and where you have supply gaps (zero results).
      </p>

      {err && <div className="lp2app__alert">{err}</div>}

      <form
        className="lp2app__card lp2app__form"
        onSubmit={(e) => {
          e.preventDefault();
          load();
        }}
      >
        <div className="lp2app__row2">
          <label className="lp2app__label">
            Days back
            <input
              className="lp2app__input"
              type="number"
              min={1}
              max={365}
              value={daysBack}
              onChange={(e) => setDaysBack(Number(e.target.value))}
            />
          </label>

          <label className="lp2app__label">
            Top N
            <input
              className="lp2app__input"
              type="number"
              min={3}
              max={50}
              value={topN}
              onChange={(e) => setTopN(Number(e.target.value))}
            />
          </label>
        </div>

        <div className="lp2app__row2">
          <label className="lp2app__label">
            Latest rows
            <input
              className="lp2app__input"
              type="number"
              min={10}
              max={2000}
              value={maxRows}
              onChange={(e) => setMaxRows(Number(e.target.value))}
            />
          </label>

          <label className="lp2app__label">
            Outward (optional)
            <input
              className="lp2app__input"
              type="text"
              value={outward}
              onChange={(e) => setOutward(e.target.value)}
              placeholder="e.g. IP9"
            />
          </label>
        </div>

        <div className="lp2app__row2">
          <label className="lp2app__label">
            Size (optional)
            <input
              className="lp2app__input"
              type="text"
              value={sizeUk}
              onChange={(e) => setSizeUk(e.target.value)}
              placeholder="e.g. 3.5"
            />
          </label>

          <label className="lp2app__label">
            ShoeType (optional 0-4)
            <input
              className="lp2app__input"
              type="number"
              min={0}
              max={4}
              value={shoeType}
              onChange={(e) => setShoeType(e.target.value)}
              placeholder="e.g. 3"
            />
          </label>
        </div>

        <div className="lp2app__row2">
          <label
            className="lp2app__label"
            style={{ display: "flex", gap: 10, alignItems: "flex-end" }}
          >
            <span style={{ display: "block" }}>Only zero-results</span>
            <select
              className="lp2app__select"
              value={onlyZeroResults ? "true" : "false"}
              onChange={(e) => setOnlyZeroResults(e.target.value === "true")}
            >
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </label>

          <div className="lp2app__actions" style={{ justifyContent: "flex-start" }}>
            <button className="lp2app__btnPrimary" type="submit" disabled={busy}>
              {busy ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>

        {/* Small hint if user typed invalid values */}
        {(shoeType.trim() && parsedShoeType === undefined) && (
          <div className="lp2app__help" style={{ marginTop: 10, color: "rgba(255,255,255,0.75)" }}>
            ShoeType must be an integer between 0 and 4.
          </div>
        )}
        {(sizeUk.trim() && parsedSizeUk === undefined) && (
          <div className="lp2app__help" style={{ marginTop: 6, color: "rgba(255,255,255,0.75)" }}>
            Size must be a valid number (e.g. 3.5).
          </div>
        )}
      </form>

      {/* KPI tiles */}
      <div className="lp2app__card" style={{ marginTop: 18 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 12,
          }}
        >
          <Kpi label="Searches" value={k ? String(k.totalSearches) : "—"} />
          <Kpi
            label="Distinct postcodes (v2 later)"
            value={k ? String(k.distinctPostcodes) : "—"}
          />
          <Kpi label="Zero-result rate" value={k ? `${k.zeroResultRatePct.toFixed(2)}%` : "—"} />
          <Kpi label="Avg latency" value={k ? `${k.avgDurationMs.toFixed(1)} ms` : "—"} />
          <Kpi label="P95 latency" value={k?.p95DurationMs != null ? `${k.p95DurationMs} ms` : "—"} />
        </div>
      </div>

      {/* Tables */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: 18,
          marginTop: 18,
        }}
      >
        <MiniTable
          title="Top sizes"
          cols={["Size", "Searches", "Zero %"]}
          rows={(data?.topSizes ?? []).map((r) => [
            String(r.sizeUk),
            String(r.searches),
            `${r.zeroRatePct.toFixed(1)}%`,
          ])}
        />

        <MiniTable
          title="Top outward codes"
          cols={["Outward", "Searches", "Zero %"]}
          rows={(data?.topOutwardCodes ?? []).map((r) => [
            r.outwardCode,
            String(r.searches),
            `${r.zeroRatePct.toFixed(1)}%`,
          ])}
        />

        <MiniTable
          title="Supply gaps (zero-result volume)"
          cols={["Size*", "Zero searches", "Zero %"]}
          rows={(data?.supplyGaps ?? []).map((r) => [
            String(r.sizeUk),
            String(r.zeroResultSearches),
            `${r.zeroRatePct.toFixed(1)}%`,
          ])}
          />
      </div>

      {/* Latest */}
      <div className="lp2app__card" style={{ marginTop: 18 }}>
        <h3 style={{ marginTop: 0 }}>Latest searches</h3>

        <div style={{ overflowX: "auto" }}>
          <table className="lp2app__table">
            <thead>
              <tr>
                <th>When</th>
                <th>Postcode</th>
                <th>Outward</th>
                <th>Size</th>
                <th>Radius</th>
                <th>Type</th>
                <th>Min cond</th>
                <th>Results</th>
                <th>Ms</th>
              </tr>
            </thead>
            <tbody>
              {(data?.latest ?? []).map((r) => (
                <tr key={r.searchId}>
                  <td>{new Date(r.createdUtc).toLocaleString()}</td>
                  <td>{r.postcodeNoSpace}</td>
                  <td>{r.outwardCode ?? "—"}</td>
                  <td>{r.sizeUk}</td>
                  <td>{r.radiusMiles}</td>
                  <td>{r.shoeType ?? "—"}</td>
                  <td>{r.minCondition ?? "—"}</td>
                  <td>{r.resultCount}</td>
                  <td>{r.durationMs}</td>
                </tr>
              ))}
              {!busy && (data?.latest?.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={9} style={{ opacity: 0.8, padding: 16 }}>
                    No rows in this window.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const Kpi: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="lp2app__card" style={{ margin: 0 }}>
    <div className="lp2app__help">{label}</div>
    <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>{value}</div>
  </div>
);

const MiniTable: React.FC<{ title: string; cols: string[]; rows: string[][]; footer?: string }> = ({
  title,
  cols,
  rows,
  footer,
}) => (
  <div className="lp2app__card" style={{ margin: 0 }}>
    <h3 style={{ marginTop: 0 }}>{title}</h3>
    <div style={{ overflowX: "auto" }}>
      <table className="lp2app__table">
        <thead>
          <tr>{cols.map((c) => <th key={c}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => <td key={j}>{c}</td>)}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={cols.length} style={{ opacity: 0.8, padding: 12 }}>
                No data
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
    {footer && (
      <div className="lp2app__help" style={{ marginTop: 10, opacity: 0.8 }}>
        {footer}
      </div>
    )}
  </div>
);

export default AdminSearchAudit;
